/**
 * VerificationResult — the result panel after a verify attempt.
 *
 * Central WOW moment: a giant green check (or red X) expands into
 * view, followed by the findings list and lineage tree. Respects
 * reduced-motion — reduces to the final static state.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { HashDisplay } from '@/components/HashDisplay';
import type { VerificationFinding, VerificationResult } from '@/api/types';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useLocaleStore } from '@/stores/localeStore';
import { formatAbsoluteTime, normalizeDomain } from '@/utils/format';

import { LineageTree } from './LineageTree';

export function VerificationResultView({ result }: { result: VerificationResult }) {
  const { t } = useTranslation(['verify', 'common']);
  const reduced = useReducedMotion();
  const locale = useLocaleStore((s) => s.locale);
  const normalized = normalizeDomain(result.manifest.domain);
  const domainLabel =
    typeof result.manifest.domain === 'string'
      ? t(`domain.${result.manifest.domain}`, { ns: 'common' })
      : result.manifest.domain.Custom;

  return (
    <div className="flex flex-col gap-6">
      {/* Hero — verdict stamp */}
      <div
        role="status"
        aria-live="polite"
        className="relative flex flex-col items-center gap-3 overflow-hidden rounded-xl border border-border bg-surface px-6 py-8 text-center"
      >
        <AnimatePresence>
          <motion.span
            key={result.ok ? 'ok' : 'fail'}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: reduced ? 0 : 0.5,
              ease: 'easeOut',
            }}
            className={
              result.ok
                ? 'flex h-20 w-20 items-center justify-center rounded-full bg-success/10 text-success'
                : 'flex h-20 w-20 items-center justify-center rounded-full bg-danger/10 text-danger'
            }
          >
            {result.ok ? <CheckBig /> : <XBig />}
          </motion.span>
        </AnimatePresence>

        <h2 className="text-xl font-semibold text-ink">
          {result.ok ? t('result.ok_title') : t('result.fail_title')}
        </h2>
        <p className="max-w-md text-sm text-ink-muted">
          {result.ok ? t('result.ok_body') : t('result.fail_body')}
        </p>
      </div>

      {/* Identity card */}
      <Card>
        <div className="flex items-start gap-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
          >
            <DomainIcon domain={normalized} className="h-6 w-6" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <DomainBadge domain={normalized} label={domainLabel} />
            <h3 className="text-lg font-semibold text-ink">{result.manifest.name}</h3>
            <p className="text-sm text-ink-muted">{result.manifest.description}</p>
            <p className="text-[11px] text-ink-subtle">
              v{result.manifest.version} · {result.manifest.author.name} ·{' '}
              {formatAbsoluteTime(result.signed_entity.created_at.iso, locale)}
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              <HashDisplay value={result.signed_entity.manifest_hash} />
              <HashDisplay value={result.signed_entity.signer_public_key} />
            </div>
          </div>
        </div>
      </Card>

      {/* Findings */}
      <Card>
        <CardTitle>{t('findings.title')}</CardTitle>
        <CardSubtitle>{t('findings.sub')}</CardSubtitle>
        <ul className="mt-3 flex flex-col gap-2">
          {result.findings.map((f, i) => (
            <FindingRow key={i} finding={f} />
          ))}
        </ul>
      </Card>

      {/* Lineage */}
      <Card>
        <CardTitle>{t('lineage.title')}</CardTitle>
        <CardSubtitle>{t('lineage.sub')}</CardSubtitle>
        <div className="mt-3">
          <LineageTree lineage={result.lineage} />
        </div>
      </Card>
    </div>
  );
}

function FindingRow({ finding }: { finding: VerificationFinding }) {
  const { t } = useTranslation('verify');
  const isOk =
    finding.kind === 'signature_valid' ||
    finding.kind === 'chain_ok' ||
    finding.kind === 'contract_hash_ok' ||
    finding.kind === 'wasm_hash_ok' ||
    finding.kind === 'timestamp_ok';
  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className={
          isOk
            ? 'mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-success/15 text-success'
            : 'mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-danger/15 text-danger'
        }
      >
        {isOk ? '✓' : '!'}
      </span>
      <div className="flex-1 text-sm">
        <p className={isOk ? 'text-ink' : 'text-danger'}>
          {t(`findings.kind.${finding.kind}`)}
        </p>
        <FindingDetail finding={finding} />
      </div>
    </li>
  );
}

function FindingDetail({ finding }: { finding: VerificationFinding }) {
  switch (finding.kind) {
    case 'signature_invalid':
    case 'chain_broken':
    case 'revoked_key':
      return (
        <p className="text-xs text-ink-muted">
          {'reason' in finding ? finding.reason : undefined}
        </p>
      );
    case 'contract_hash_mismatch':
    case 'wasm_hash_mismatch':
      return (
        <p className="text-xs text-ink-muted">
          expected <HashDisplay value={finding.expected} /> · got{' '}
          <HashDisplay value={finding.actual} />
        </p>
      );
    default:
      return null;
  }
}

function CheckBig() {
  return (
    <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path d="M5 12l4 4L19 7" />
    </svg>
  );
}

function XBig() {
  return (
    <svg viewBox="0 0 24 24" className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
